import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCompleteFragment } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Heading,
  Icon,
  Stack,
  Textarea,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';

import { sessionTypeFragment } from './session-type.operations';

const updateSessionTypeMutation = graphql(`
  mutation updateSessionType(
    $tenantId: uuid!
    $id: uuid!
    $context: String
    $isInitialVisitType: Boolean!
  ) {
    updateSessionTypesByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: { context: $context, isInitialVisitType: $isInitialVisitType }
    ) {
      __typename
      id
      context
      isInitialVisitType
    }
  }
`);

const formSchema = z.object({
  context: z.string().optional(),
  isInitialVisitType: z.boolean().optional(),
});

type Form = z.infer<typeof formSchema>;

export interface SessionTypesActionCellProps {
  tenantId: string;
  id: string;
}

export const SessionTypesActionCell: React.FC<SessionTypesActionCellProps> = ({
  tenantId,
  id,
}) => {
  const logger = useNamedLogger('SessionTypesActionCell');
  const [isOpen, setIsOpen] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  const [updateSessionType] = useMutation(updateSessionTypeMutation, {
    onCompleted: () => {
      toast.success(t('tenantAdmin.sessionTypes.updated'));
      setIsOpen(false);
    },
    onError: () => toast.error(t('tenantAdmin.sessionTypes.updateFailed')),
    optimisticResponse: ({ context, isInitialVisitType }) => ({
      updateSessionTypesByPk: {
        __typename: 'SessionTypes' as const,
        id,
        isInitialVisitType,
        context: context ?? null,
      },
    }),
  });

  const onSubmit = async ({ context, isInitialVisitType = false }: Form) => {
    await updateSessionType({
      variables: {
        tenantId,
        id,
        context: context ?? null,
        isInitialVisitType,
      },
    });
  };

  const sessionType = useCompleteFragment({
    fragment: sessionTypeFragment,
    key: {
      id,
    },
  });

  const form = useForm<Form>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      context: sessionType.context ?? undefined,
      isInitialVisitType: sessionType.isInitialVisitType ?? false,
    },
  });

  return (
    <>
      <Stack direction="horizontal" fullWidth={false} gap={2}>
        <SimpleTooltip label={t('tenantAdmin.sessionTypes.editTooltip')}>
          <Button size="sm" variant="outline">
            <Icon name="Pencil" size="lg" onClick={() => setIsOpen(true)} />
          </Button>
        </SimpleTooltip>
      </Stack>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <Form {...form}>
          <DialogContent
            size="lg"
            onInteractOutside={(e) => e.preventDefault()}
          >
            <Heading size="3xl">
              {t('tenantAdmin.sessionTypes.updateTitle')}
            </Heading>
            <Heading size="2xl" weight="semibold">
              {sessionType.name}
            </Heading>
            <form
              onSubmit={form.handleSubmit(onSubmit, (invalid) =>
                logger.debug(
                  `Invalid form submission attempted: ${JSON.stringify(invalid)}`,
                ),
              )}
              className="flex flex-col gap-4"
            >
              <FormField
                control={form.control}
                name="isInitialVisitType"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormLabel>
                      {t('tenantAdmin.sessionTypes.isInitialVisitTypeLabel')}
                    </FormLabel>
                    <FormControl>
                      <Checkbox
                        className="m-0"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="context"
                render={({ field, fieldState: { error } }) => (
                  <FormItem className="max-w-lg">
                    <FormLabel>
                      {t('tenantAdmin.sessionTypes.contextColumn')}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        className="bg-white/5"
                        placeholder={t(
                          'tenantAdmin.sessionTypes.contextPlaceholder',
                        )}
                        {...field}
                      />
                    </FormControl>
                    {error && (
                      <FormMessage className="mt-4">
                        {error.message}
                      </FormMessage>
                    )}
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button variant={'outline'} onClick={() => setIsOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={!form.formState.isDirty}>
                  {t('tenantAdmin.common.saveChanges')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Form>
      </Dialog>
    </>
  );
};
